import { PublicPageShell } from '@/components/layout/public-page-shell';
import { Button } from '@/components/ui/button';
import {
  CONSENT_LINK_COPY,
  CONSENT_PAGE_FAILURE_COPY,
} from '@/features/recruitment/lib/consent-link-copy';
import {
  loadConsentPage,
  submitConsent,
  submitWithdraw,
  type ConsentPageUpdate,
  type ConsentPagePhase,
} from '@/features/recruitment/lib/consent-page';
import { confirmConsent, getConsentLink, withdrawConsent } from '@/lib/consent-public';
import { cn } from '@/lib/utils';
import { createFileRoute } from '@tanstack/react-router';
import { AlertCircle, CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';

// HW-1024: public, unauthenticated page behind the confirm link in a
// consent-request email. Opening it grants nothing — mail scanners open links
// on their own — consent is recorded only when the candidate presses the button.
// HW-1057: once consent is given, the same page lets the candidate withdraw it,
// again only by pressing a button (and confirming).
// No `beforeLoad` — that omission is what bypasses the app's auth redirect.
export const Route = createFileRoute('/consent/$token')({
  component: ConsentConfirmPage,
});

const API = { getConsentLink, confirmConsent, withdrawConsent };

// The company-wide policy on hygge.software; the email links the same page.
const PRIVACY_POLICY_URL = 'https://hygge.software/privacy-policy-and-personal-data-processing';

function ConsentConfirmPage() {
  const { token } = Route.useParams();
  const [phase, setPhase] = useState<ConsentPagePhase>('loading');
  const [contactEmail, setContactEmail] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Bumped by "Try again" to re-read the link after a failed load.
  const [attempt, setAttempt] = useState(0);
  // HW-1057: "Withdraw consent" asks once more before it acts.
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);

  const apply = (update: ConsentPageUpdate) => {
    if (update.contactEmail !== undefined) setContactEmail(update.contactEmail);
    if (update.phase !== undefined) setPhase(update.phase);
    if (update.submitError !== undefined) setSubmitError(update.submitError);
  };

  useEffect(() => {
    let active = true;
    void loadConsentPage(API, token).then((update) => {
      if (active) apply(update);
    });
    return () => {
      active = false;
    };
  }, [token, attempt]);

  const retry = () => {
    setPhase('loading');
    setAttempt((n) => n + 1);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      apply(await submitConsent(API, token));
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === 'loading') {
    return (
      <PublicPageShell>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-hygge" />
        </div>
      </PublicPageShell>
    );
  }

  const handleWithdraw = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      apply(await submitWithdraw(API, token));
    } finally {
      setSubmitting(false);
      setConfirmWithdraw(false);
    }
  };

  // HW-1057: a given consent, or one that has ended, can be withdrawn here.
  if (phase === 'CONFIRMED' || phase === 'ENDED') {
    const confirmed = CONSENT_LINK_COPY[phase];
    return (
      <PublicPageShell>
        <div className="mx-auto flex max-w-[440px] flex-col items-center gap-4 px-4 py-24 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
            {phase === 'CONFIRMED' ? (
              <CheckCircle2 className="h-8 w-8 text-hygge" />
            ) : (
              <AlertCircle className="h-8 w-8 text-muted-foreground" />
            )}
          </div>
          <h1 className="text-xl font-semibold tracking-tight">{confirmed.title}</h1>
          {phase === 'ENDED' ? (
            <p className="text-sm text-muted-foreground">{confirmed.message}</p>
          ) : (
            <p className="text-sm text-muted-foreground">
              We keep your data under our{' '}
              <a
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-hygge underline underline-offset-2"
              >
                privacy policy
              </a>
              . You can withdraw your consent at any time — we then stop using your data and erase
              it.
            </p>
          )}
          {confirmWithdraw ? (
            <div className="flex flex-col items-center gap-2">
              <p className="text-sm font-medium">Withdraw your consent?</p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirmWithdraw(false)}
                  disabled={submitting}
                >
                  Keep my consent
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleWithdraw}
                  disabled={submitting}
                >
                  {submitting ? 'Withdrawing…' : 'Yes, withdraw'}
                </Button>
              </div>
            </div>
          ) : (
            <Button type="button" variant="outline" onClick={() => setConfirmWithdraw(true)}>
              Withdraw consent
            </Button>
          )}
          {submitError ? <p className="text-sm text-destructive">{submitError}</p> : null}
          {contactEmail ? (
            <ContactLine email={contactEmail} prefix="Questions? Write to us at" />
          ) : null}
        </div>
      </PublicPageShell>
    );
  }

  if (phase === 'PENDING') {
    return (
      <PublicPageShell>
        <div className="mx-auto flex max-w-[520px] flex-col gap-5 px-4 py-16">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
            <ShieldCheck className="h-7 w-7 text-hygge" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Consent to store your data</h1>
          <p className="text-sm text-muted-foreground">
            The Hygge recruitment team is processing your application and needs your permission to
            retain your personal data under our current{' '}
            <a
              href={PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-hygge underline underline-offset-2"
            >
              privacy policy
            </a>
            . Press the button below to give your consent.
          </p>
          <div className="flex flex-col gap-2">
            <Button
              type="button"
              variant="accent"
              size="lg"
              className="w-full sm:w-auto sm:self-start"
              onClick={handleConfirm}
              disabled={submitting}
            >
              {submitting ? 'Saving…' : 'I consent'}
            </Button>
            {submitError ? <p className="text-sm text-destructive">{submitError}</p> : null}
          </div>
          {contactEmail ? (
            <ContactLine email={contactEmail} prefix="Questions? Write to us at" />
          ) : null}
        </div>
      </PublicPageShell>
    );
  }

  const copy =
    phase === 'FAILED' ||
    phase === 'RATE_LIMITED' ||
    phase === 'UNSUPPORTED' ||
    phase === 'WITHDRAWN'
      ? CONSENT_PAGE_FAILURE_COPY[phase]
      : CONSENT_LINK_COPY[phase];
  const Icon = copy.tone === 'success' ? CheckCircle2 : AlertCircle;

  return (
    <PublicPageShell>
      <div className="mx-auto flex max-w-[440px] flex-col items-center gap-4 px-4 py-24 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <Icon
            className={cn(
              'h-8 w-8',
              copy.tone === 'success' ? 'text-hygge' : 'text-muted-foreground'
            )}
          />
        </div>
        <h1 className="text-xl font-semibold tracking-tight">{copy.title}</h1>
        <p className="text-sm text-muted-foreground">{copy.message}</p>
        {phase === 'FAILED' ? (
          <Button type="button" variant="outline" onClick={retry}>
            Try again
          </Button>
        ) : null}
        {/* HW-1057: after a withdrawal the address stays, for questions. */}
        {copy.tone === 'success' && phase !== 'WITHDRAWN' ? null : contactEmail ? (
          <ContactLine email={contactEmail} prefix="Write to us at" />
        ) : (
          // The address comes from the server; when the page could not reach
          // it, the email's Reply-To still goes to the recruiting mailbox.
          <p className="text-sm text-muted-foreground">You can reply to the email we sent you.</p>
        )}
      </div>
    </PublicPageShell>
  );
}

// The address is the link's own visible text, not hidden behind a label:
// mailto is unreliable in some mail apps' in-app browsers, so it must stay
// readable and copyable.
function ContactLine({ email, prefix }: { email: string; prefix: string }) {
  return (
    <p className="text-sm text-muted-foreground">
      {prefix}{' '}
      <a
        href={`mailto:${email}`}
        className="font-medium text-foreground underline-offset-4 hover:underline"
      >
        {email}
      </a>
    </p>
  );
}
